// API Service for World Metal Bands backend
import { API_CONFIG, CATEGORY_MAPPING, THEME_MAPPING, DEFAULT_API_PARAMS, ERROR_MESSAGES } from '../config/api';
import cacheService from './cache';
import { getCurrentLanguage } from '../utils/apiHeaders';

const { BASE_URL, API_KEY, TIMEOUT, RETRY_ATTEMPTS, RETRY_DELAY } = API_CONFIG;

// Default headers for all API requests
const getHeaders = (language = null) => ({
  'Content-Type': 'application/json',
  'Accept-Language': language || getCurrentLanguage(),
  'x-api-key': API_KEY,
});

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    let errorMessage;
    
    switch (response.status) {
      case 401:
        errorMessage = ERROR_MESSAGES.UNAUTHORIZED;
        break;
      case 404:
        errorMessage = ERROR_MESSAGES.NOT_FOUND;
        break;
      case 429:
        errorMessage = ERROR_MESSAGES.RATE_LIMIT;
        break;
      case 500:
      case 502:
      case 503:
        errorMessage = ERROR_MESSAGES.SERVER_ERROR;
        break;
      default:
        errorMessage = ERROR_MESSAGES.API_ERROR;
    }
    
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorMessage);
  }
  return response.json();
};

// Helper function to make API requests with retry logic
const apiRequest = async (endpoint, options = {}, language = null) => {
  const url = `${BASE_URL}${endpoint}`;
  const config = {
    ...options,
    headers: {
      ...getHeaders(language),
      ...options.headers,
    },
    signal: AbortSignal.timeout(TIMEOUT)
  };

  let lastError;
  
  for (let attempt = 1; attempt <= RETRY_ATTEMPTS; attempt++) {
    try {
      const response = await fetch(url, config);
      return await handleResponse(response);
    } catch (error) {
      lastError = error;
      
      // Don't retry on certain errors
      if (error.name === 'AbortError') {
        throw new Error(ERROR_MESSAGES.TIMEOUT);
      }
      
      if (error.message.includes('401') || error.message.includes('404')) {
        throw error;
      }
      
      // Wait before retry (except on last attempt)
      if (attempt < RETRY_ATTEMPTS) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * attempt));
      }
    }
  }
  
  throw lastError || new Error(ERROR_MESSAGES.NETWORK_ERROR);
};

// Category mapping for different video categories
export const VIDEO_CATEGORIES = {
  TRENDING_NOW: 'trending',
  POPULAR_MOVIES: 'popular',
  NEW_RELEASES: 'new-releases',
  MY_LIST: 'my-list',
  CONTINUE_WATCHING: 'continue-watching',
  FEATURED: 'featured'
};

const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';

/**
 * Fetch videos by category
 * @param {string} category - Category from VIDEO_CATEGORIES
 * @param {number} limit - Number of videos to fetch (default: 8)
 * @param {number} offset - Offset for pagination (default: 0)
 * @param {string} search - Search term (optional)
 * @param {string} label - Label filter (optional)
 * @param {string} theme - Theme to use for API calls (optional)
 * @param {string} language - Language code for API requests (optional)
 * @param {string} orderfields - Field to order by (default: 'created')
 * @param {string} orderby - Order direction (default: 'desc')
 * @param {boolean} useCache - Whether to use cache (default: true)
 * @returns {Promise<Object>} API response with videos
 */
export const fetchVideosByCategory = async ({
  category,
  limit = DEFAULT_API_PARAMS.limit,
  offset = DEFAULT_API_PARAMS.offset,
  search = DEFAULT_API_PARAMS.search,
  label = DEFAULT_API_PARAMS.label,
  theme = null,
  language = null,
  orderfields = DEFAULT_API_PARAMS.orderfields,
  orderby = DEFAULT_API_PARAMS.orderby,
  useCache = !isLocalhost // Réactiver le cache
}) => {
  // Déterminer le paramètre 't' basé sur le thème
  const themeParam = theme && THEME_MAPPING[theme] ? THEME_MAPPING[theme] : DEFAULT_API_PARAMS.t;
  
  // Déterminer la langue à utiliser
  const langParam = language || getCurrentLanguage();
  
  // Créer une clé de cache qui inclut le thème ET la langue
  const cacheKey = `${category}_${themeParam}_${langParam}`;
  
  // Vérifier le cache en premier
  if (useCache) {
    const cached = cacheService.get(cacheKey, { limit, offset, search, label, orderfields, orderby });
    if (cached) {
      console.log(`💾 Cache hit for ${category} with theme ${theme || 'default'} and language ${langParam}`);
      return cached;
    }
  }

  // Map category to API category
  const apiCategory = CATEGORY_MAPPING[category] || category;

  const params = new URLSearchParams({
    label: apiCategory,
    limit: limit.toString(),
    offset: offset.toString(),
    search,
    orderfields,
    orderby,
    t: themeParam
  });

  const endpoint = `/blogs?${params.toString()}`;
  
  console.log(`🔍 API Request for ${category} with theme ${theme || 'default'} and language ${langParam}:`, `${BASE_URL}${endpoint}`);
  
  const response = await apiRequest(endpoint, {}, language);

  // Mettre en cache la réponse avec la clé incluant le thème ET la langue
  if (useCache) {
    cacheService.set(cacheKey, { limit, offset, search, label, orderfields, orderby }, response);
    console.log(`💾 Cached response for ${category} with theme ${theme || 'default'} and language ${langParam}`);
  }

  return response;
};

/**
 * Fetch a specific video by its pathseo
 * @param {string} pathseo - The SEO path of the video
 * @param {string} v - Version parameter (optional)
 * @param {string} fb - Facebook parameter (optional)
 * @returns {Promise<Object>} API response with video details
 */
export const fetchVideoByPathseo = async (pathseo, v = null, fb = null) => {
  const params = new URLSearchParams();
  if (v !== null) params.append('v', v);
  if (fb !== null) params.append('fb', fb);
  
  const queryString = params.toString();
  const endpoint = `/blog/${pathseo}${queryString ? `?${queryString}` : ''}`;
  
  return await apiRequest(endpoint);
};

/**
 * Transform API video data to match our application format
 * @param {Object} apiVideo - Video data from API
 * @returns {Object} Transformed video data
 */
export const transformVideoData = (apiVideo, themeParam = 'metal') => {
  // Extract YouTube video ID from metadata if available
  const getYouTubeUrl = (videoId) => {
    return videoId ? `https://www.youtube.com/watch?v=${videoId}` : '';
  };

  // Get the best available thumbnail
  const getBestThumbnail = (thumbnails) => {
    if (!thumbnails) return '';
    return thumbnails.maxres?.url || 
           thumbnails.standard?.url || 
           thumbnails.high?.url || 
           thumbnails.medium?.url || 
           thumbnails.default?.url || '';
  };

  // Get the best backdrop (use maxres or high quality thumbnail)
  const getBackdrop = (thumbnails) => {
    if (!thumbnails) return '';
    return thumbnails.maxres?.url || thumbnails.standard?.url || '';
  };

  // Extract duration from description or use default
  const extractDuration = (description) => {
    // Try to extract duration from description if available
    // For now, use a default value since API doesn't provide duration
    return 180; // 3 minutes default
  };

  // Calculate vote average from video engagement (mock calculation)
  const calculateVoteAverage = (channelTitle, publishedAt) => {
    // Mock calculation based on channel reputation and recency
    const channelRatings = {
      'SUMERIAN': 8.5,
      'Century Media Records': 8.2,
      'Nuclear Blast Records': 8.7,
      'Earache Records': 7.8,
      'Season of Mist': 8.0,
      'RelapseRecords': 8.3,
      'Metal Blade Records': 8.1
    };
    
    const baseRating = channelRatings[channelTitle] || 7.0;
    
    // Add some variance based on recency
    const publishDate = new Date(publishedAt);
    const now = new Date();
    const daysDiff = (now - publishDate) / (1000 * 60 * 60 * 24);
    const recencyBonus = Math.max(0, (30 - daysDiff) / 300); // Newer content gets slight boost
    
    return Math.min(10, Math.max(1, baseRating + recencyBonus));
  };

  // Mock vote count based on channel size
  const calculateVoteCount = (channelTitle) => {
    const channelSizes = {
      'SUMERIAN': 15000,
      'Century Media Records': 12000,
      'Nuclear Blast Records': 18000,
      'Earache Records': 8000,
      'Season of Mist': 10000,
      'RelapseRecords': 14000,
      'Metal Blade Records': 11000
    };
    
    const baseCount = channelSizes[channelTitle] || 5000;
    return Math.floor(baseCount + Math.random() * 5000);
  };

  const thumbnails = apiVideo.metadata?.snippet?.thumbnails;
  const metadata = apiVideo.metadata || {};

  return {
    id: apiVideo._id || Math.random().toString(36),
    title: metadata.title || 'Untitled',
    name: metadata.title || 'Untitled', // For compatibility
    overview: metadata.description || 'No description available',
    backdrop_path: getBackdrop(thumbnails),
    poster_path: getBestThumbnail(thumbnails),
    release_date: metadata.publishedAt || new Date().toISOString(),
    first_air_date: metadata.publishedAt || new Date().toISOString(),
    vote_average: calculateVoteAverage(metadata.channelTitle, metadata.publishedAt),
    runtime: extractDuration(metadata.description),
    // Cherche le genre_ids dans le tableau de labels du thème courant
    genre_ids: (() => {
      // Récupère le tableau de labels du thème courant
      try {
        const { getThemeConfig } = require('../config/themes');
        const themeLabels = getThemeConfig(themeParam)?.labels || [];
        // console.log('themeLabels', themeLabels);
        // Cherche le label correspondant
        const labelToFind = metadata.username || '';
        const foundLabel = themeLabels.find(l => l.label === labelToFind);
        return foundLabel?.genre_ids || [6666666];

      } catch (e) {
        return [14];
      }
    })(),
    trailerUrl: getYouTubeUrl(metadata.videoId),
    vote_count: calculateVoteCount(metadata.channelTitle),
    
    // Additional fields from API
    pathseo: apiVideo.pathseo || '',
    videoId: metadata.videoId || '',
    channelTitle: metadata.channelTitle || '',
    username: metadata.username || '',
    publishedAt: metadata.publishedAt || '',
    titleSeo: metadata.titleSeo || '',
    parsed: apiVideo.parsed || null,
    
    // Computed fields
    artist: metadata.channelTitle || 'Unknown Artist',
    label: metadata.channelTitle || '',
    created: metadata.publishedAt || new Date().toISOString(),
    updated: apiVideo.parsed || new Date().toISOString(),
    
  // Compatibility fields
  category: themeParam, // Utilise le paramètre du thème courant
    tags: metadata.genre || [],
    views: calculateVoteCount(metadata.channelTitle), // Use vote_count as views
    likes: Math.floor(calculateVoteCount(metadata.channelTitle) * 0.1), // 10% of views as likes
    
    // YouTube specific
    youtubeData: {
      videoId: metadata.videoId,
      channelTitle: metadata.channelTitle,
      username: metadata.username,
      thumbnails: thumbnails,
      publishedAt: metadata.publishedAt
    }
  };
};

/**
 * Fetch and transform videos by category
 * @param {string} category - Category from VIDEO_CATEGORIES
 * @param {Object} options - Additional options for the API call
 * @returns {Promise<Object>} Object with videos array and pagination info
 */
export const getVideosByCategory = async (category, options = {}) => {
  try {
    const response = await fetchVideosByCategory({
      category,
      ...options
    });
    
    // Transform the API response to match our format
    const videos = response.data || [];
  // Déterminer le paramètre 't' basé sur le thème
  const theme = options.theme || null;
  const themeParam = theme && THEME_MAPPING[theme] ? THEME_MAPPING[theme] : DEFAULT_API_PARAMS.t;
  const transformedVideos = videos.map(video => transformVideoData(video, themeParam));
    
    // Extract pagination info
    const pagination = response.pgination || response.pagination || {};
    
    return {
      videos: transformedVideos,
      pagination: {
        totalDocs: pagination.totalDocs || 0,
        offset: pagination.offset || 0,
        limit: pagination.limit || 8,
        totalPages: pagination.totalPages || 1,
        page: pagination.page || 1,
        hasNextPage: pagination.hasNextPage || false,
        hasPrevPage: pagination.hasPrevPage || false,
        count: transformedVideos.length
      },
      meta: {
        count: response.count || transformedVideos.length,
        origin: response.origin || 'api'
      }
    };
  } catch (error) {
    console.error(`Failed to fetch videos for category ${category}:`, error);
    throw error;
  }
};

/**
 * Function to clear cache when theme changes
 * @param {string} newTheme - The new theme that was selected
 */
export const onThemeChange = (newTheme) => {
  console.log(`🎨 Theme changed to: ${newTheme}`);
  
  // Clear the entire cache to force fresh API calls with new theme
  cacheService.clear();
  
  console.log(`🗑️ Cache cleared due to theme change`);
};

/**
 * Fetch and transform a specific video
 * @param {string} pathseo - The SEO path of the video
 * @returns {Promise<Object>} Transformed video data
 */
export const getVideoByPathseo = async (pathseo) => {
  try {
    const response = await fetchVideoByPathseo(pathseo);
    
    // The API might return the video directly or in a data wrapper
    const videoData = response.data || response;
    
  // Déterminer le paramètre 't' basé sur le thème (optionnel, ici on peut utiliser le défaut)
  return transformVideoData(videoData);
  } catch (error) {
    console.error(`Failed to fetch video with pathseo ${pathseo}:`, error);
    throw error;
  }
};

/**
 * Clear cache (useful when language changes)
 */
export const clearCache = () => {
  console.log('🗑️ Clearing API cache');
  cacheService.clear();
};

// Export the API service
const apiService = {
  fetchVideosByCategory,
  fetchVideoByPathseo,
  getVideosByCategory,
  getVideoByPathseo,
  transformVideoData,
  onThemeChange,
  clearCache,
  VIDEO_CATEGORIES
};

export default apiService;
